import { CHAT_MS_BASE_URL } from 'src/services/customBaseQuery';
import { api } from 'src/services/api';
import {
  appendFormData,
  formatMessagesResponse,
  getHeaders,
  getQueryString
} from 'src/services/utils';
import { DataApiInputParams } from 'src/types/api';

export const CHAT_LIST_PAGE_SIZE = 30;
export const MESSAGE_PAGE_TURNS = 20;

export const chatApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getChats: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const url = `${CHAT_MS_BASE_URL}chat?${getQueryString(arg.params)}`;
        const result: any = await fetchWithBQ({
          url,
          ...getHeaders(url, null, true),
          ms: true
        });
        if (result.error) return result;
        return {
          data: {
            ...result.data,
            data: Array.isArray(result?.data?.data) ? result.data.data : [],
            page: arg.params?.p ?? 0,
            pageSize: arg.params?.ps ?? CHAT_LIST_PAGE_SIZE
          }
        };
      },

      serializeQueryArgs: ({ queryArgs }) => {
        const { p, ps, ...cleanParams } = queryArgs.params ?? {};
        return {
          endpointName: 'getChats',
          params: cleanParams
        };
      },

      merge: (currentCache, newPage) => {
        if (newPage.page === 0) {
          currentCache.data = newPage.data;
        } else {
          const existingIds = new Set(currentCache.data.map((c: any) => c.id));
          const additions = newPage.data.filter(
            (c: any) => !existingIds.has(c.id)
          );
          currentCache.data.push(...additions);
        }
        currentCache.page = newPage.page;
        currentCache.pc = newPage.pc;
      },

      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.params?.p !== previousArg?.params?.p;
      },

      providesTags: ['Chats']
    }),

    createChat: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const formData = appendFormData(arg.params);
        const url = `${CHAT_MS_BASE_URL}chat`;
        return fetchWithBQ({
          url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      },
      invalidatesTags: ['Chats']
    }),

    renameChat: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const formData = appendFormData(arg.params);
        const url = `${CHAT_MS_BASE_URL}chat`;
        return fetchWithBQ({
          url,
          method: 'PATCH',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      },
      invalidatesTags: ['Chats']
    }),

    deleteChat: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const formData = appendFormData(arg.params);
        const url = `${CHAT_MS_BASE_URL}chat`;
        return fetchWithBQ({
          url,
          method: 'DELETE',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      },
      invalidatesTags: ['Chats']
    }),

    getMessages: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const { server_chat_id: serverChatId, ...params } = arg.params ?? {};
        const isClientKey = String(params.chat_id ?? '').startsWith('new-');
        const fetchChatId = isClientKey ? serverChatId : params.chat_id;
        if (!fetchChatId) {
          return {
            data: {
              data: { msgs: [] },
              nextCursor: null
            }
          };
        }
        const url = `${CHAT_MS_BASE_URL}chat-msg?${getQueryString({
          ...params,
          chat_id: fetchChatId,
          limit: params.limit ?? MESSAGE_PAGE_TURNS
        })}`;
        const result: any = await fetchWithBQ({
          url,
          ...getHeaders(url, null, true),
          ms: true
        });
        if (result.error) return result;
        const formatted = formatMessagesResponse(result);
        const page = formatted?.data?.data;
        return {
          data: {
            ...formatted.data,
            nextCursor: page?.next_cursor ?? null
          }
        };
      },

      serializeQueryArgs: ({ queryArgs }) => {
        const { cursor, limit, server_chat_id, ...rest } =
          queryArgs.params ?? {};
        return {
          endpointName: 'getMessages',
          params: rest
        };
      },

      merge: (currentCache, newPage, { arg }) => {
        if (!arg.params?.cursor) {
          currentCache.data = newPage.data;
        } else {
          const newMsgs = newPage?.data?.msgs ?? [];
          const existingMsgs = currentCache?.data?.msgs ?? [];
          const existingIds = new Set(existingMsgs.map((m: any) => m.id));
          const additions = newMsgs.filter((m: any) => !existingIds.has(m.id));
          currentCache.data.msgs = [...additions, ...existingMsgs];
        }
        currentCache.nextCursor = newPage.nextCursor;
      },

      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.params?.cursor !== previousArg?.params?.cursor;
      },

      providesTags: ['Messages']
    }),

    postMessage: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const formData = appendFormData(arg.params);
        const url = `${CHAT_MS_BASE_URL}chat-msg`;
        return fetchWithBQ({
          url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      }
    }),

    cancelMessage: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const formData = appendFormData(arg.params);
        const url = `${CHAT_MS_BASE_URL}cancel`;
        return fetchWithBQ({
          url,
          method: 'POST',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      }
    }),

    patchMessage: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const formData = appendFormData(arg.params);
        const url = `${CHAT_MS_BASE_URL}chat-msg`;
        return fetchWithBQ({
          url,
          method: 'PATCH',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      }
    }),
    patchChatSettings: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const formData = appendFormData(arg.params);
        const url = `${CHAT_MS_BASE_URL}chat-resources`;
        return fetchWithBQ({
          url,
          method: 'PATCH',
          body: formData,
          ...getHeaders(url, formData, true),
          ms: true
        });
      }
    })
  })
});

export const {
  useGetChatsQuery,
  useLazyGetChatsQuery,
  useCreateChatMutation,
  useRenameChatMutation,
  useDeleteChatMutation,
  useGetChatParamsQuery,
  useGetMessagesQuery,
  useLazyGetMessagesQuery,
  usePostMessageMutation,
  useCancelMessageMutation,
  usePatchMessageMutation,
  usePatchChatSettingsMutation
} = chatApi;
